import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getPublicAppSettingsApi } from '@app/api/setting.api';
import { setAppCurrency } from '@app/utils/utils';
import logoFallback from '@app/assets/logo.jpg';

interface AppSettingContextValue {
  logoUrl: string;
  appName: string;
  storeName: string;
  storePhone: string;
  storeAddress: string;
  storeWebsite: string;
  storeLegalName: string;
  storeTaxCode: string;
  payslipSignerName: string;
  onepayEnable: boolean;
  /** Đơn vị tiền toàn app (setting `currency`, tab Cửa hàng): 'VND' | 'USD'. */
  currency: string;
  refresh: () => void;
}

const AppSettingContext = createContext<AppSettingContextValue>({
  logoUrl: logoFallback,
  appName: '',
  storeName: '',
  storePhone: '',
  storeAddress: '',
  storeWebsite: '',
  storeLegalName: '',
  storeTaxCode: '',
  payslipSignerName: '',
  onepayEnable: false,
  currency: 'VND',
  refresh: () => undefined,
});

const getSetting = (settings: any[], key: string): string =>
  settings.find((s) => s.key === key)?.value ?? '';

export const AppSettingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [logoUrl, setLogoUrl] = useState<string>(logoFallback);
  const [appName, setAppName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storeWebsite, setStoreWebsite] = useState('');
  const [storeLegalName, setStoreLegalName] = useState('');
  const [storeTaxCode, setStoreTaxCode] = useState('');
  const [payslipSignerName, setPayslipSignerName] = useState('');
  const [onepayEnable, setOnepayEnable] = useState(false);
  const [currency, setCurrency] = useState('VND');

  const load = useCallback(async () => {
    try {
      const res = await getPublicAppSettingsApi();
      if (res?.logo_url) setLogoUrl(res.logo_url);
      const s = res?.settings ?? [];
      const app = getSetting(s, 'app_name');
      if (app) setAppName(app);
      const name = getSetting(s, 'store_name') || app;
      if (name) setStoreName(name);
      const phone = getSetting(s, 'store_phone');
      if (phone) setStorePhone(phone);
      const address = getSetting(s, 'store_address');
      if (address) setStoreAddress(address);
      const website = getSetting(s, 'store_website');
      if (website) setStoreWebsite(website);
      setStoreLegalName(getSetting(s, 'store_legal_name'));
      setStoreTaxCode(getSetting(s, 'store_tax_code'));
      setPayslipSignerName(getSetting(s, 'payslip_signer_name'));
      setOnepayEnable(getSetting(s, 'onepay_enable') === 'true');
      const currencySetting = getSetting(s, 'currency') || 'VND';
      setCurrency(currencySetting);
      setAppCurrency(currencySetting);
    } catch {
      // silently fall back to defaults
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppSettingContext.Provider
      value={{ logoUrl, appName, storeName, storePhone, storeAddress, storeWebsite, storeLegalName, storeTaxCode, payslipSignerName, onepayEnable, currency, refresh: load }}
    >
      {children}
    </AppSettingContext.Provider>
  );
};

export const useAppSetting = () => useContext(AppSettingContext);
